import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  EmailAccount,
  EmailTemplate,
  EmailLog,
  SendEmailRequest,
  SendEmailResponse,
  SendTestEmailRequest,
  SendOrderNotificationRequest,
} from '../../types/email';
import type {
  Quote,
  QuoteItem,
  QuoteFormData,
} from '../../types/quotes';
import type { RootState } from '../index';
import type {
  HealthResponse,
  AuthResponse,
  LoginRequest,
  LogoutRequest,
  TokenRefreshRequest,
  TokenRefreshResponse,
  User,
  Role,
  RoleSummary,
  Permission,
  PermissionSummary,
  PermissionCategory,
  PermissionsByCategory,
  PaginatedResponse,
  RoleFormData,
  RolePermissionAssignment,
  UserProfile,
  UserProfileFormData,
  UserActivity,
  UserStatusChangeRequest,
  UserRoleAssignmentRequest,
  ColorPalette,
  // Jobs types
  Job,
  JobSummary,
  JobFormData,
  JobAssignDeliveryRequest,
  JobStatusUpdateRequest,
  JobAddNoteRequest,
  // Production types
  ProductionJobSummary,
  ProductionSummary,
  ProductionHistoryEntry,
  MoveToProductionRequest,
  UpdateProductionStatusRequest,
  GetProductionJobsParams,
  BulkCompleteProductionLineResponse,
  // Orders types
  Order,
  OrderSummary,
  OrderFormData,
  // Holiday types
  BankHoliday,
  BankHolidayFormData,
  HolidayRequest,
  HolidayRequestFormData,
  AdminHolidayRequestFormData,
  HolidayRequestStatusUpdate,
  UserHolidayStats,
  AllUsersHolidayStatsResponse,
  MyHolidayRequestsResponse,
  // Delivery postcodes
  BulkAddDeliveryPostcodesRequest,
  BulkAddDeliveryPostcodesResponse,
  DeliveryPostcodeDetail,
  GroupedDeliveryPostcodesResponse,
  // Email types
} from '../../utils/types';
import { APP_CONFIG, API_URLS, buildApiUrl } from '../../utils/constants';

/**
 * Production Line, Glass Arrival, and Delivery Jobs list endpoints use pagination_class=None
 * (bare JSON array). Legacy responses used PaginatedResponse. Normalize so consumers keep using .results.
 */
function normalizeUnpaginatedJobList<T>(response: unknown): PaginatedResponse<T> {
  if (Array.isArray(response)) {
    const results = response as T[];
    return { count: results.length, next: null, previous: null, results };
  }
  const r = response as PaginatedResponse<T>;
  const results = r?.results ?? [];
  return {
    count: r?.count ?? results.length,
    next: r?.next ?? null,
    previous: r?.previous ?? null,
    results,
  };
}

/** Max page_size for holiday list endpoints (backend CustomPageNumberPagination cap). */
export const HOLIDAY_FETCH_PAGE_SIZE = 10000;

function normalizeBankHolidaysList(response: unknown): BankHoliday[] {
  if (Array.isArray(response)) {
    return response as BankHoliday[];
  }
  const r = response as PaginatedResponse<BankHoliday>;
  return r?.results ?? [];
}

/**
 * Merge a full Job (e.g. PATCH / getJob response) into a ProductionJobSummary row
 * so Production board updates without refetching the whole list.
 */
function mergeProductionSummaryFromJob(row: ProductionJobSummary, job: Job): void {
  const customerName = `${job.customer_first_name ?? ''} ${job.customer_last_name ?? ''}`.trim();
  if (customerName.length > 0) {
    row.customer_name = customerName;
  }

  const j = job as Job & { production_status_display?: string };
  if (j.website_job_number !== undefined) row.website_job_number = j.website_job_number;
  if (j.customer_email !== undefined) row.customer_email = j.customer_email;
  if (j.customer_mobile_number !== undefined) row.customer_mobile_number = j.customer_mobile_number;
  if (j.total_amount !== undefined) row.total_amount = j.total_amount;
  if (j.total_items !== undefined) row.total_items = j.total_items;
  if (j.production_status !== undefined) row.production_status = j.production_status as ProductionJobSummary['production_status'];
  if (j.production_status_display !== undefined) row.production_status_display = j.production_status_display;
  if (j.delivery_postcode !== undefined) row.delivery_postcode = j.delivery_postcode;
  if (j.fulfillment_type !== undefined) row.fulfillment_type = j.fulfillment_type;
  if (j.pallet_type !== undefined) row.pallet_type = j.pallet_type;
  if (j.glass_weight_kg !== undefined) row.glass_weight_kg = j.glass_weight_kg;
  if (j.small_van !== undefined) row.small_van = j.small_van;
  if (j.pallet_stage !== undefined) row.pallet_stage = j.pallet_stage;
  if (j.pallet_loaded !== undefined) row.pallet_loaded = j.pallet_loaded;
  if (j.priority !== undefined) row.priority = j.priority;
  if (j.created_at !== undefined) row.created_at = j.created_at;
  if (j.created_by_username !== undefined) row.created_by_username = j.created_by_username;
  if (j.delivery_user !== undefined) row.delivery_user = j.delivery_user;
  if (j.delivery_user_username !== undefined) row.delivery_user_username = j.delivery_user_username;
  if (j.glass_arrival_date !== undefined) row.glass_arrival_date = j.glass_arrival_date;
  if (j.manufacture_date !== undefined) row.manufacture_date = j.manufacture_date;
  if (j.potential_delivery_date !== undefined) row.potential_delivery_date = j.potential_delivery_date;
  if (j.pallet_delivery_date !== undefined) row.pallet_delivery_date = j.pallet_delivery_date;
  if (j.pallet_delivery_email_sent_at !== undefined) row.pallet_delivery_email_sent_at = j.pallet_delivery_email_sent_at;
  if (j.pallet_delivery_email_sent_by !== undefined) row.pallet_delivery_email_sent_by = j.pallet_delivery_email_sent_by;
  if (j.pallet_delivery_email_sent_by_username !== undefined) {
    row.pallet_delivery_email_sent_by_username = j.pallet_delivery_email_sent_by_username;
  }
  if (j.production_notes !== undefined) row.production_notes = j.production_notes;
  if (j.job_type !== undefined) row.job_type = j.job_type;
  if (j.ordered_and_checked !== undefined) row.ordered_and_checked = j.ordered_and_checked;
  if (j.ordered_checked_by !== undefined) row.ordered_checked_by = j.ordered_checked_by;
  if (j.ordered_checked_at !== undefined) row.ordered_checked_at = j.ordered_checked_at;
  if (j.ordered_checked_by_username !== undefined) row.ordered_checked_by_username = j.ordered_checked_by_username;
  if (j.review_email_sent_by !== undefined) row.review_email_sent_by = j.review_email_sent_by;
  if (j.review_email_sent_at !== undefined) row.review_email_sent_at = j.review_email_sent_at;
  if (j.review_email_sent_by_username !== undefined) row.review_email_sent_by_username = j.review_email_sent_by_username;
  if (j.order_email_sent_by !== undefined) row.order_email_sent_by = j.order_email_sent_by;
  if (j.order_email_sent_at !== undefined) row.order_email_sent_at = j.order_email_sent_at;
  if (j.order_email_sent_by_username !== undefined) row.order_email_sent_by_username = j.order_email_sent_by_username;
  if (j.in_production_email_sent_by !== undefined) row.in_production_email_sent_by = j.in_production_email_sent_by;
  if (j.in_production_email_sent_at !== undefined) row.in_production_email_sent_at = j.in_production_email_sent_at;
  if (j.in_production_email_sent_by_username !== undefined) row.in_production_email_sent_by_username = j.in_production_email_sent_by_username;
  if (j.lock_data !== undefined) row.lock_data = j.lock_data;
  if (j.has_quotation_product !== undefined) row.has_quotation_product = j.has_quotation_product;
}

/** Set after createApi; used by patchJob / sendOrderNotification onQueryStarted. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let prodMergeApi: any;

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: APP_CONFIG.API_URL,
    prepareHeaders: (headers, { getState, endpoint }) => {
      // Skip authentication for login and refresh endpoints only
      // Note: logout needs the access token to properly log out the user
      const skipAuthEndpoints = ['login', 'refreshToken'];
      
      if (!skipAuthEndpoints.includes(endpoint)) {
        // Get token from Redux state for better integration
        const token = (getState() as RootState).auth.accessToken;
        if (token) {
          headers.set('authorization', `Bearer ${token}`);
        }
      }
      return headers;
    },
  }),
  tagTypes: [
    'Health', 'Auth', 'User', 'Role', 'Permission', 'UserProfile', 'UserActivity', 
    'Job', 'ProductionJob', 'ProductionSummary', 'Order', 'OrderImage',
    'ProductionLineDashboard', 'ProductionLineDashboardAnalytics',
    'ProductionLineSealing', 
    'ProductionLineGlassCutting', 'ProductionLineSpacer', 'ProductionLineDrawing',
    'GlassArrival', 'DeliveryJobs', 'Delivery', 'Collection',
    'EmailAccount', 'EmailTemplate', 'EmailLog',
    'ColorPalette', 'Quote',
    'HolidayRequest', 'BankHoliday', 'HolidayStats',
    'DeliveryPostcode',
    'GlassValue',
  ],
  keepUnusedDataFor: 300, // Keep data for 5 minutes
  endpoints: builder => ({
    // Health endpoint (existing)
    getHealth: builder.query<HealthResponse, void>({
      query: () => '/health/',
      providesTags: ['Health'],
      // Only refetch if data is older than 30 seconds
      keepUnusedDataFor: 30,
    }),

    // Authentication endpoints
    login: builder.mutation<AuthResponse, LoginRequest>({
      query: credentials => ({
        url: '/auth/login/',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['Auth', 'User'],
    }),

    logout: builder.mutation<{ message: string }, LogoutRequest>({
      query: ({ refresh }) => ({
        url: '/auth/logout/',
        method: 'POST',
        body: { refresh },
      }),
      invalidatesTags: ['Auth', 'User'],
    }),

    getUserProfile: builder.query<{
      user: User;
      role_details?: { id: string; name: string; description: string } | null;
      profile?: { id: string; status: string } | null;
    }, void>({
      query: () => '/auth/profile/',
      providesTags: ['User'],
      // Keep user profile data for 10 minutes
      keepUnusedDataFor: 600,
    }),

    refreshToken: builder.mutation<TokenRefreshResponse, TokenRefreshRequest>({
      query: ({ refresh }) => ({
        url: '/auth/token/refresh/',
        method: 'POST',
        body: { refresh },
      }),
    }),

    // ==============================================================================
    // ROLE MANAGEMENT ENDPOINTS
    // ==============================================================================

    // Get all roles with optional filtering and pagination
    getRoles: builder.query<PaginatedResponse<RoleSummary>, {
      page?: number;
      page_size?: number;
      search?: string;
      is_active?: boolean;
      ordering?: string;
    }>({
      query: (params = {}) => ({
        url: '/roles/',
        params: Object.fromEntries(
          Object.entries(params).filter(([_, value]) => value !== undefined)
        ),
      }),
      providesTags: ['Role'],
    }),

    // Get role details by ID
    getRole: builder.query<Role, string>({
      query: (id) => `/roles/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Role', id }],
    }),

    // Create new role
    createRole: builder.mutation<Role, RoleFormData>({
      query: (roleData) => ({
        url: '/roles/',
        method: 'POST',
        body: roleData,
      }),
      invalidatesTags: ['Role'],
    }),

    // Update existing role
    updateRole: builder.mutation<Role, { id: string; data: RoleFormData }>({
      query: ({ id, data }) => ({
        url: `/roles/${id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Role', id }, 'Role'],
    }),

    // Delete role
    deleteRole: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/roles/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Role'],
    }),

    // Get role permissions
    getRolePermissions: builder.query<{
      role: { id: string; name: string; description: string };
      permissions: Array<{
        id: string;
        permission: PermissionSummary;
        assigned_by: string | null;
        created_at: string;
      }>;
      total_permissions: number;
    }, string>({
      query: (roleId) => `/roles/${roleId}/permissions/`,
      providesTags: (result, error, roleId) => [{ type: 'Role', id: roleId }],
    }),

    // Assign permissions to role
    assignRolePermissions: builder.mutation<{
      message: string;
      role_id: string;
      permissions_assigned: number;
    }, { roleId: string; permissionIds: string[] }>({
      query: ({ roleId, permissionIds }) => ({
        url: `/roles/${roleId}/assign_permissions/`,
        method: 'POST',
        body: { permission_ids: permissionIds },
      }),
      invalidatesTags: (result, error, { roleId }) => [{ type: 'Role', id: roleId }],
    }),

    // Get users assigned to role
    getRoleUsers: builder.query<{
      role: { id: string; name: string; description: string };
      users: Array<any>; // Define UserProfile type when needed
      total_users: number;
    }, string>({
      query: (roleId) => `/roles/${roleId}/users/`,
      providesTags: (result, error, roleId) => [{ type: 'Role', id: roleId }],
    }),

    // ==============================================================================
    // PERMISSION MANAGEMENT ENDPOINTS
    // ==============================================================================

    // Get all permissions with filtering
    getPermissions: builder.query<PaginatedResponse<PermissionSummary>, {
      page?: number;
      page_size?: number;
      search?: string;
      category?: string;
      is_active?: boolean;
      ordering?: string;
    }>({
      query: (params = {}) => ({
        url: '/permissions/',
        params: Object.fromEntries(
          Object.entries(params).filter(([_, value]) => value !== undefined)
        ),
      }),
      providesTags: ['Permission'],
    }),

    // Get permission details by ID
    getPermission: builder.query<Permission, string>({
      query: (id) => `/permissions/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Permission', id }],
    }),

    // Get permission categories with counts
    getPermissionCategories: builder.query<{
      categories: PermissionCategory[];
      total_categories: number;
    }, void>({
      query: () => '/permissions/categories/',
      providesTags: ['Permission'],
    }),

    // Get permissions grouped by category
    getPermissionsByCategory: builder.query<{
      categories: PermissionsByCategory[];
      total_categories: number;
      total_permissions: number;
    }, void>({
      query: () => '/permissions/by_category/',
      providesTags: ['Permission'],
    }),

    // ==============================================================================
    // USER PROFILE MANAGEMENT ENDPOINTS
    // ==============================================================================

    // Get all user profiles with filtering and pagination
    getUserProfiles: builder.query<PaginatedResponse<UserProfile>, {
      page?: number;
      page_size?: number;
      search?: string;
      status?: 'active' | 'inactive' | 'blocked' | 'deleted';
      role?: string;
      ordering?: string;
    }>({
      query: (params = {}) => ({
        url: '/user-profiles/',
        params: Object.fromEntries(
          Object.entries(params).filter(([_, value]) => value !== undefined)
        ),
      }),
      providesTags: ['UserProfile'],
      // Always refetch when component mounts to ensure fresh data
      // This fixes the issue where new delivery users don't appear in dropdowns
      refetchOnMountOrArgChange: true,
      // Don't keep unused data for long - refetch on next mount
      keepUnusedDataFor: 0,
    }),

    // Get user profile details by ID
    getUserProfileById: builder.query<UserProfile, string>({
      query: (id) => `/user-profiles/${id}/`,
      providesTags: (result, error, id) => [{ type: 'UserProfile', id }],
    }),

    // Create new user profile
    createUserProfile: builder.mutation<UserProfile, UserProfileFormData>({
      query: (userData) => ({
        url: '/user-profiles/',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: ['UserProfile'],
    }),

    // Update existing user profile
    updateUserProfile: builder.mutation<UserProfile, { id: string; data: Partial<UserProfileFormData> }>({
      query: ({ id, data }) => ({
        url: `/user-profiles/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'UserProfile', id }, 'UserProfile'],
    }),

    // Delete user profile
    deleteUserProfile: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/user-profiles/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserProfile'],
    }),

    // Assign role to user profile
    assignUserRole: builder.mutation<{
      message: string;
      user_id: number;
      role: { id: string; name: string; description: string };
    }, { profileId: string; roleId: string }>({
      query: ({ profileId, roleId }) => ({
        url: `/user-profiles/${profileId}/assign_role/`,
        method: 'POST',
        body: { role_id: roleId },
      }),
      invalidatesTags: (result, error, { profileId }) => [
        { type: 'UserProfile', id: profileId }, 
        'UserProfile'
      ],
    }),

    // Change user profile status
    changeUserStatus: builder.mutation<{
      message: string;
      user_id: number;
      old_status: string;
      new_status: string;
    }, { profileId: string; status: 'active' | 'inactive' }>({
      query: ({ profileId, status }) => ({
        url: `/user-profiles/${profileId}/change_status/`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (result, error, { profileId }) => [
        { type: 'UserProfile', id: profileId }, 
        'UserProfile'
      ],
    }),

    softDeleteUserProfile: builder.mutation<
      { message: string; profile: UserProfile },
      { profileId: string }
    >({
      query: ({ profileId }) => ({
        url: `/user-profiles/${profileId}/soft_delete/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (result, error, { profileId }) => [
        { type: 'UserProfile', id: profileId },
        'UserProfile',
      ],
    }),

    // ==============================================================================
    // USER ACTIVITY ENDPOINTS
    // ==============================================================================

    // Get user activities with filtering
    getUserActivities: builder.query<PaginatedResponse<UserActivity>, {
      page?: number;
      page_size?: number;
      search?: string;
      action?: string;
      user?: number;
      ordering?: string;
    }>({
      query: (params = {}) => ({
        url: '/user-activities/',
        params: Object.fromEntries(
          Object.entries(params).filter(([_, value]) => value !== undefined)
        ),
      }),
      providesTags: ['UserActivity'],
    }),

    // Get activity summary
    getActivitySummary: builder.query<{
      total_activities: number;
      period: string;
      action_breakdown: Array<{ action: string; count: number }>;
      most_active_users: Array<{ user__username: string; count: number }>;
      generated_at: string;
    }, void>({
      query: () => '/user-activities/summary/',
      providesTags: ['UserActivity'],
    }),

    // ==============================================================================
    // USER VALIDATION ENDPOINTS
    // ==============================================================================

    validateUsername: builder.query<{ is_unique: boolean; username?: string; exists?: boolean }, { username: string; exclude_user_id?: number }>(
      {
        query: ({ username, exclude_user_id }) => ({
          url: '/user-profiles/validate_username/',
          params: Object.fromEntries(
            Object.entries({ username, exclude_user_id }).filter(([, v]) => v !== undefined && v !== '')
          ),
        }),
        // Force fresh data, don't cache validation results
        keepUnusedDataFor: 0,
        // Skip cache altogether for validation
        forceRefetch: ({ currentArg, previousArg }) => currentArg !== previousArg,
      }
    ),

    validateEmail: builder.query<{ is_unique: boolean; email?: string; exists?: boolean }, { email: string; exclude_user_id?: number }>(
      {
        query: ({ email, exclude_user_id }) => ({
          url: '/user-profiles/validate_email/',
          params: Object.fromEntries(
            Object.entries({ email, exclude_user_id }).filter(([, v]) => v !== undefined && v !== '')
          ),
        }),
        // Force fresh data, don't cache validation results
        keepUnusedDataFor: 0,
        // Skip cache altogether for validation
        forceRefetch: ({ currentArg, previousArg }) => currentArg !== previousArg,
      }
    ),

    // ==============================================================================
    // COLOR PALETTE ENDPOINTS
    // ==============================================================================

    // Get all colors
    getColors: builder.query<ColorPalette[], void>({
      query: () => '/colors/',
      providesTags: ['ColorPalette'],
    }),

    // Get available colors (not assigned to any user)
    getAvailableColors: builder.query<ColorPalette[], void>({
      query: () => '/colors/available/',
      providesTags: ['ColorPalette'],
    }),

    // Assign color to user
    assignUserColor: builder.mutation<{
      message: string;
      color: ColorPalette;
      user_profile: UserProfile;
    }, { colorId: string; userProfileId: string }>({
      query: ({ colorId, userProfileId }) => ({
        url: `/colors/${colorId}/assign_to_user/`,
        method: 'POST',
        body: { user_profile_id: userProfileId },
      }),
      invalidatesTags: ['ColorPalette', 'UserProfile'],
    }),

    // Remove color from user
    removeUserColor: builder.mutation<{
      message: string;
      color: ColorPalette;
      user_profile: UserProfile;
    }, { colorId: string; userProfileId: string }>({
      query: ({ colorId, userProfileId }) => ({
        url: `/colors/${colorId}/remove_from_user/`,
        method: 'POST',
        body: { user_profile_id: userProfileId },
      }),
      invalidatesTags: ['ColorPalette', 'UserProfile'],
    }),

    // ==============================================================================
    // JOBS ENDPOINTS
    // ==============================================================================

    getJobs: builder.query<PaginatedResponse<JobSummary>, {
      page?: number;
      page_size?: number;
      search?: string;
      status?: string;
      status__in?: string;
      source_type?: string;
      fulfillment_type?: string;
      delivery_user?: string;
      pallet_stage?: string;
      created_by?: string;
      created_at_after?: string;
      created_at_before?: string;
      website_job_number?: string;
      ordering?: string;
    }>({
      query: (params = {}) => ({
        url: '/jobs/',
        params: Object.fromEntries(
          Object.entries(params).filter(([_, value]) => value !== undefined && value !== '')
        ),
      }),
      providesTags: ['Job'],
    }),

    getJob: builder.query<Job, string>({
      query: (id) => `/jobs/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Job', id }],
    }),

    createJob: builder.mutation<Job, JobFormData>({
      query: (data) => ({ url: '/jobs/', method: 'POST', body: data }),
      invalidatesTags: ['Job'],
    }),

    updateJob: builder.mutation<Job, { id: string; data: JobFormData }>({
      query: ({ id, data }) => ({ url: `/jobs/${id}/`, method: 'PUT', body: data }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Job', id }, 'Job'],
    }),

    patchJob: builder.mutation<Job, { id: string; data: Partial<JobFormData> }>({
      query: ({ id, data }) => ({ url: `/jobs/${id}/`, method: 'PATCH', body: data }),
      // Include list tag so getJobs (e.g. Pallet Management) refetches; detail tag for getJob.
      invalidatesTags: (result, error, { id }) => [{ type: 'Job', id }, 'Job'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          if (!prodMergeApi) return;
          const queryArgs = prodMergeApi.util.selectCachedArgsForQuery(
            getState(),
            'getProductionJobs'
          ) as GetProductionJobsParams[];
          for (const qa of queryArgs) {
            dispatch(
              prodMergeApi.util.updateQueryData(
                'getProductionJobs',
                qa,
                (draft: PaginatedResponse<ProductionJobSummary>) => {
                  const row = draft.results?.find((j) => j.id === data.id);
                  if (row) mergeProductionSummaryFromJob(row, data);
                }
              )
            );
          }
        } catch {
          /* PATCH failed */
        }
      },
    }),

    deleteJob: builder.mutation<{ message?: string }, string>({
      query: (id) => ({ url: `/jobs/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['Job'],
    }),

    remakeJob: builder.mutation<Job, { id: string }>({
      query: ({ id }) => ({ url: `/jobs/${id}/remake/`, method: 'POST' }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Job', id },
        'Job',
        'ProductionJob',
        'Order',
        ...(result?.id ? [{ type: 'Job' as const, id: result.id }] : []),
      ],
    }),

    assignDelivery: builder.mutation<Job, { id: string; data: JobAssignDeliveryRequest }>({
      query: ({ id, data }) => ({ url: `/jobs/${id}/assign_delivery/`, method: 'POST', body: data }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Job', id }, 
        { type: 'ProductionJob', id }, 
        'Job', 
        'ProductionJob'
      ],
    }),
 
    updateJobStatus: builder.mutation<Job, { id: string; data: JobStatusUpdateRequest }>({
      query: ({ id, data }) => ({ url: `/jobs/${id}/status/`, method: 'POST', body: data }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Job', id }, 'Job'],
    }),

    addJobNote: builder.mutation<Job, { id: string; data: JobAddNoteRequest }>({
      query: ({ id, data }) => ({ url: `/jobs/${id}/add_note/`, method: 'POST', body: data }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Job', id }, 'Job'],
    }),
    
    // Calendar-specific jobs endpoint
    getCalendarJobs: builder.query<JobSummary[], { start_date: string; end_date: string; delivery_user?: string; fulfillment_type?: string }>({
      query: ({ start_date, end_date, delivery_user, fulfillment_type }) => ({
        url: '/jobs/',
        params: {
          potential_delivery_date__gte: start_date,
          potential_delivery_date__lte: end_date,
          delivery_user__isnull: false, // Require delivery_user to be set
          ...(delivery_user && delivery_user !== 'all' ? { delivery_user } : {}),
          ...(fulfillment_type && fulfillment_type !== 'all' ? { fulfillment_type } : {}),
          page_size: 1000, // Get all jobs in range (no pagination for calendar)
        },
      }),
      providesTags: ['Job'],
      transformResponse: (response: PaginatedResponse<JobSummary>) => response.results,
    }),
    
    rescheduleJob: builder.mutation<Job, { id: string; potential_delivery_date: string }>({
      query: ({ id, potential_delivery_date }) => ({
        url: `/jobs/${id}/reschedule/`,
        method: 'POST',
        body: { potential_delivery_date },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Job', id },
        'Job', // Invalidate all jobs (including calendar)
        'DeliveryJobs', // Invalidate delivery jobs list
      ],
    }),

    lockJob: builder.mutation<Job, { id: string; is_locked: boolean; reason?: string }>({
      query: ({ id, is_locked, reason }) => ({
        url: `/jobs/${id}/lock/`,
        method: 'POST',
        body: { is_locked, reason },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Job', id },
        'Job',
        'ProductionJob',
      ],
    }),

    // ============================================================================
    // PRODUCTION ENDPOINTS
    // ============================================================================

    getProductionJobs: builder.query<PaginatedResponse<ProductionJobSummary>, GetProductionJobsParams>({
      query: (params) => ({
        url: '/production-jobs/',
        params,
      }),
      transformResponse: (response: PaginatedResponse<ProductionJobSummary>) => {
        // Ensure has_quotation_product is preserved (API returns snake_case)
        const results = (response?.results ?? []).map((job) => ({
          ...job,
          has_quotation_product: job.has_quotation_product ?? false,
        }));
        return { ...response, results };
      },
      providesTags: (result) => [
        'ProductionJob',
        ...(result?.results?.map(({ id }) => ({ type: 'ProductionJob' as const, id })) ?? []),
      ],
    }),

    getProductionSummary: builder.query<ProductionSummary, void>({
      query: () => '/production-jobs/summary/',
      providesTags: ['ProductionSummary'],
    }),

    markPalletBooked: builder.mutation<
      ProductionJobSummary,
      { id: string; pallet_delivery_date?: string | null }
    >({
      query: ({ id, pallet_delivery_date }) => ({
        url: `/production-jobs/${id}/mark_pallet_booked/`,
        method: 'POST',
        body: pallet_delivery_date ? { pallet_delivery_date } : {},
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ProductionJob', id },
        'ProductionJob',
        { type: 'Job', id },
        'Job',
      ],
    }),

    moveToProduction: builder.mutation<Job, MoveToProductionRequest>({
      query: ({ id, ...body }) => ({
        url: `/jobs/${id}/move_to_production/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Job', id },
        'Job',
        'ProductionJob',
        'ProductionSummary',
      ],
    }),

    updateProductionStatus: builder.mutation<Job, UpdateProductionStatusRequest>({
      query: ({ id, ...body }) => ({
        url: `/jobs/${id}/update_production_status/`,
        method: 'POST', 
        body,
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled, getState }) {
        if (!prodMergeApi) return;
        const state = getState();
        const queryArgs = prodMergeApi.util.selectCachedArgsForQuery(
          state,
          'getProductionJobs'
        ) as GetProductionJobsParams[];
        const patchResults: Array<{ undo: () => void }> = [];
        for (const qa of queryArgs) {
          const cached = prodMergeApi.endpoints.getProductionJobs.select(qa)(state);
          const row = cached.data?.results?.find((j) => j.id === id);
          if (
            row &&
            qa.production_status !== undefined &&
            qa.production_status === row.production_status
          ) {
            const patch = dispatch(
              prodMergeApi.util.updateQueryData(
                'getProductionJobs',
                qa,
                (draft: PaginatedResponse<ProductionJobSummary>) => {
                  if (draft.results) {
                    draft.results = draft.results.filter((j) => j.id !== id);
                  }
                }
              )
            );
            if (patch && typeof (patch as { undo?: () => void }).undo === 'function') {
              patchResults.push(patch as { undo: () => void });
            }
          }
        }
        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((p) => p.undo());
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Job', id },
        { type: 'ProductionJob', id },
        'ProductionJob',
        'ProductionSummary',
      ],
    }),

    completeProduction: builder.mutation<
      any,
      { jobId: string; completion_source?: 'job_edit_skip' | 'production_dashboard' | 'bulk_dashboard' }
    >({
      query: ({ jobId, completion_source }) => ({
        url: `/jobs/${jobId}/complete_production/`,
        method: 'POST',
        body: completion_source ? { completion_source } : {},
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: 'Job', id: jobId },
        { type: 'ProductionJob', id: jobId },
        'Job',
        'ProductionJob',
        'ProductionSummary',
        'ProductionLineSealing',
        'ProductionLineGlassCutting',
        'ProductionLineSpacer',
        'ProductionLineDrawing',
        'ProductionLineDashboard',
        'GlassArrival'
      ],
    }),

    moveBackToProduction: builder.mutation<any, { jobId: string; notes?: string }>({
      query: ({ jobId, notes }) => ({
        url: `/jobs/${jobId}/move_back_to_production/`,
        method: 'POST',
        body: notes ? { notes } : {}
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: 'Job', id: jobId },
        { type: 'ProductionJob', id: jobId },
        'Job',
        'ProductionJob',
        'ProductionSummary',
        'ProductionLineSealing',
        'ProductionLineGlassCutting',
        'ProductionLineSpacer',
        'ProductionLineDrawing',
        'ProductionLineDashboard',
        'GlassArrival',
        'Order'
      ],
    }),

    // ============================================================================
    // PRODUCTION LINE ENDPOINTS
    // ============================================================================

    getProductionLineDashboard: builder.query<PaginatedResponse<ProductionLineJobSummary>, GetProductionLineParams>({
      query: (params) => ({
        url: '/production-line/dashboard/',
        params,
      }),
      transformResponse: normalizeUnpaginatedJobList<ProductionLineJobSummary>,
      providesTags: ['ProductionLineDashboard'],
      // Optimize for performance with many orders
      keepUnusedDataFor: 60, // Keep data for 1 minute only
    }),

    bulkCompleteProductionLineDashboard: builder.mutation<
      BulkCompleteProductionLineResponse,
      { jobIds: string[] }
    >({
      query: ({ jobIds }) => ({
        url: '/production-line/dashboard/bulk-complete-production/',
        method: 'POST',
        body: { job_ids: jobIds },
      }),
      invalidatesTags: () => [
        'ProductionLineDashboard',
        'ProductionLineSealing',
        'ProductionLineGlassCutting',
        'ProductionLineSpacer',
        'ProductionLineDrawing',
        'GlassArrival',
        'Job',
        'ProductionJob',
        'ProductionSummary',
      ],
    }),

    getProductionLineDashboardAnalytics: builder.query<any, void>({
      query: () => '/production-line/dashboard/analytics/',
      providesTags: ['ProductionLineDashboardAnalytics'],
    }),

    getProductionLineSealing: builder.query<PaginatedResponse<ProductionLineJobSummary>, GetProductionLineParams>({
      query: (params) => ({
        url: '/production-line/sealing-allocation/',
        params,
      }),
      transformResponse: normalizeUnpaginatedJobList<ProductionLineJobSummary>,
      providesTags: ['ProductionLineSealing'],
      // Refresh every time for real-time updates
      keepUnusedDataFor: 0, // Don't cache - always fetch fresh data
    }),

    getProductionLineGlassCutting: builder.query<PaginatedResponse<ProductionLineJobSummary>, GetProductionLineParams>({
      query: (params) => ({
        url: '/production-line/glass-cutting/',
        params,
      }),
      transformResponse: normalizeUnpaginatedJobList<ProductionLineJobSummary>,
      providesTags: ['ProductionLineGlassCutting'],
      keepUnusedDataFor: 0, // Always fetch fresh data for real-time updates
    }),

    getProductionLineSpacer: builder.query<PaginatedResponse<ProductionLineJobSummary>, GetProductionLineParams>({
      query: (params) => ({
        url: '/production-line/spacer-cutting/',
        params,
      }),
      transformResponse: normalizeUnpaginatedJobList<ProductionLineJobSummary>,
      providesTags: ['ProductionLineSpacer'],
      keepUnusedDataFor: 0, // Always fetch fresh data
    }),

    getProductionLineDrawing: builder.query<PaginatedResponse<ProductionLineJobSummary>, GetProductionLineParams>({
      query: (params) => ({
        url: '/production-line/drawing/',
        params,
      }),
      transformResponse: normalizeUnpaginatedJobList<ProductionLineJobSummary>,
      providesTags: ['ProductionLineDrawing'],
      keepUnusedDataFor: 0, // Always fetch fresh data
    }),

    // Glass Arrival Queries
    getGlassArrivalJobs: builder.query<PaginatedResponse<ProductionLineJobSummary>, GetProductionLineParams>({
      query: (params) => ({
        url: '/glass-arrival/',
        params,
      }),
      transformResponse: normalizeUnpaginatedJobList<ProductionLineJobSummary>,
      providesTags: ['GlassArrival'],
      keepUnusedDataFor: 0, // Always fetch fresh data
    }),

    // Delivery Jobs Queries
    getDeliveryJobs: builder.query<
      PaginatedResponse<ProductionLineJobSummary>,
      {
        potential_delivery_date__date?: string;
        delivery_user?: string;
        search?: string;
        ordering?: string;
      }
    >({
      query: (params = {}) => ({
        url: '/delivery-jobs/',
        params: Object.fromEntries(
          Object.entries(params).filter(([_, value]) => value !== undefined && value !== '')
        ),
      }),
      transformResponse: normalizeUnpaginatedJobList<ProductionLineJobSummary>,
      providesTags: (result, error, arg) => ['Job', 'DeliveryJobs'],
    }),

    bulkRescheduleDeliveryJobs: builder.mutation<
      {
        success: boolean;
        updated_count: number;
        failed_count: number;
        jobs: Array<{
          id: string;
          website_job_number: string;
          potential_delivery_date: string;
          sequence: number;
        }>;
        failed_jobs?: Array<{
          id: string;
          reason: string;
        }>;
      },
      {
        job_ids: string[];
        date: string;
      }
    >({
      query: (body) => ({
        url: '/delivery-jobs/bulk_reschedule/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DeliveryJobs', 'Job'], // Invalidates both delivery list and calendar
    }),

    // Update delivery details (window, status, notes)
    updateDeliveryDetails: builder.mutation<
      any,
      {
        id: string;
        data: {
          delivery_window_start?: number | null;
          delivery_window_end?: number | null;
          delivery_status?: string | null;
          delivery_notes?: string | null;
        };
      }
    >({
      query: ({ id, data }) => ({
        url: `/delivery-jobs/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Job', id },
        'DeliveryJobs',
      ],
    }),

    // ============================================================================
    // DELIVERY WORKFLOW ENDPOINTS
    // ============================================================================

    // Get delivery status for a job
    getCollectionStatus: builder.query<{
      collection_completed: boolean;
      collection_completed_at: string | null;
      collection_completed_by: number | null;
      collection_completed_by_username: string | null;
    }, string>({
      query: (jobId) => `/collection/${jobId}/status/`,
      providesTags: (result, error, jobId) => [{ type: 'Collection', id: jobId }],
    }),

    setCollectionCompleted: builder.mutation<
      {
        collection_completed: boolean;
        collection_completed_at: string | null;
        collection_completed_by: number | null;
        collection_completed_by_username: string | null;
      },
      { jobId: string; completed: boolean }
    >({
      query: ({ jobId, completed }) => ({
        url: `/collection/${jobId}/complete/`,
        method: 'POST',
        body: { completed },
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: 'Collection', id: jobId },
        { type: 'Job', id: jobId },
        'Job',
      ],
    }),

    getDeliveryStatus: builder.query<{
      delivery_result: 'pending' | 'delivered' | 'incomplete';
      record: {
        id: string;
        status: 'delivered' | 'incomplete';
        receiver_name?: string;
        photos: string[];
        notes: string;
        reason?: 'manufacturer_fault' | 'delivery_fault';
        completed_by_name?: string;
        completed_at: string;
      } | null;
      history_count: number;
    }, string>({
      query: (jobId) => `/delivery/${jobId}/status/`,
      providesTags: (result, error, jobId) => [{ type: 'Delivery', id: jobId }],
    }),

    // Mark delivery as complete
    submitDeliveryComplete: builder.mutation<
      any,
      {
        jobId: string;
        data: {
          receiver_name: string;
          photos: string[];
          notes: string;
          send_review?: boolean;
        };
      }
    >({
      query: ({ jobId, data }) => ({
        url: `/delivery/${jobId}/complete/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: 'Delivery', id: jobId },
        { type: 'Job', id: jobId },
        'DeliveryJobs',
      ],
    }),

    // Mark delivery as incomplete
    submitDeliveryIncomplete: builder.mutation<
      any,
      {
        jobId: string;
        data: {
          reason: 'manufacturer_fault' | 'delivery_fault';
          notes: string;
        };
      }
    >({
      query: ({ jobId, data }) => ({
        url: `/delivery/${jobId}/incomplete/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: 'Delivery', id: jobId },
        { type: 'Job', id: jobId },
        'DeliveryJobs',
      ],
    }),

    // Update existing delivery details
    updateDeliveryRecord: builder.mutation<
      any,
      {
        jobId: string;
        data: {
          receiver_name?: string;
          photos?: string[];
          notes?: string;
        };
      }
    >({
      query: ({ jobId, data }) => ({
        url: `/delivery/${jobId}/update-delivery/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: 'Delivery', id: jobId },
        { type: 'Job', id: jobId },
      ],
    }),

    // Get delivery history
    getDeliveryHistory: builder.query<
      Array<{
        id: string;
        changed_by_name?: string;
        changed_at: string;
        from_status: string;
        to_status: string;
        notes: string;
      }>,
      string
    >({
      query: (jobId) => `/delivery/${jobId}/history/`,
      providesTags: (result, error, jobId) => [{ type: 'Delivery', id: jobId }],
    }),

    // Admin override delivery status
    overrideDeliveryStatus: builder.mutation<
      {
        delivery_result: string;
        previous_status: string;
        record: any;
        history_count: number;
      },
      {
        jobId: string;
        data: {
          status: 'pending' | 'delivered' | 'incomplete';
          admin_notes: string;
        };
      }
    >({
      query: ({ jobId, data }) => ({
        url: `/delivery/${jobId}/override/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: 'Delivery', id: jobId },
        { type: 'Job', id: jobId },
        'DeliveryJobs',
      ],
    }),

    // Get presigned URL for delivery photo upload
    presignDeliveryUpload: builder.mutation<
      {
        url: string;
        fields: Record<string, string>;
        key: string;
        public_url: string;
        expires_in: number;
      },
      {
        job_id: string;
        file_type?: string;
      }
    >({
      query: (data) => ({
        url: '/core/delivery-presign/',
        method: 'POST',
        body: data,
      }),
    }),

    // Get presigned URL for viewing delivery photo
    presignDeliveryPhoto: builder.mutation<
      { url: string; expires_in: number },
      { jobId: string; photo_url: string }
    >({
      query: ({ jobId, photo_url }) => ({
        url: `/delivery/${jobId}/presign-photo/`,
        method: 'POST',
        body: { photo_url },
      }),
    }),

    // Production Line Action Mutations
    completeSealing: builder.mutation<any, { orderId: string; notes?: string }>({
      query: ({ orderId, notes }) => ({
        url: `/production-line/sealing-allocation/${orderId}/complete_sealing/`,
        method: 'POST',
        body: { notes },
      }),
      invalidatesTags: [
        'ProductionLineSealing', 'ProductionLineDashboard',
        'Job', 'Order', 'ProductionJob' // Invalidate all related caches
      ],
    }),

    sendBackSealing: builder.mutation<any, { orderId: string; reason?: string }>({
      query: ({ orderId, reason }) => ({
        url: `/production-line/sealing-allocation/${orderId}/send_back_sealing/`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: [
        'ProductionLineSealing', 'ProductionLineDashboard',
        'Job', 'Order', 'ProductionJob' // Invalidate all related caches
      ],
    }),

    updateCuttingSpecs: builder.mutation<any, { orderId: string; width?: number; height?: number; thickness?: number; cutting_notes?: string }>({
      query: ({ orderId, ...specs }) => ({
        url: `/production-line/glass-cutting/${orderId}/update_cutting_specs/`,
        method: 'POST',
        body: specs,
      }),
      invalidatesTags: ['ProductionLineGlassCutting', 'ProductionLineDashboard'],
    }),

    completeCutting: builder.mutation<any, { orderId: string; notes?: string }>({
      query: ({ orderId, notes }) => ({
        url: `/production-line/glass-cutting/${orderId}/complete_cutting/`,
        method: 'POST',
        body: { notes },
      }),
      invalidatesTags: ['ProductionLineGlassCutting', 'ProductionLineDashboard'],
    }),

    sendBackGlassCutting: builder.mutation<any, { orderId: string; reason?: string }>({
      query: ({ orderId, reason }) => ({
        url: `/production-line/glass-cutting/${orderId}/send_back_glass_cutting/`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: [
        'ProductionLineGlassCutting', 'ProductionLineDashboard',
        'Job', 'Order', 'ProductionJob' // Invalidate all related caches
      ],
    }),

    completeSpacerCutting: builder.mutation<any, { orderId: string; spacer_width?: number; spacer_length?: number; spacer_type?: string; notes?: string }>({
      query: ({ orderId, ...specs }) => ({
        url: `/production-line/spacer-cutting/${orderId}/complete_spacer_cutting/`,
        method: 'POST',
        body: specs,
      }),
      invalidatesTags: [
        'ProductionLineSpacer', 'ProductionLineDashboard', 
        'Job', 'Order', 'ProductionJob'
      ],
    }),

    attachDrawing: builder.mutation<any, { orderId: string; drawing_type?: string; notes?: string }>({
      query: ({ orderId, ...data }) => ({
        url: `/production-line/drawing/${orderId}/attach_drawing/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        'ProductionLineDrawing', 'ProductionLineDashboard',
        'Job', 'Order', 'ProductionJob'
      ],
    }),

    completeDrawing: builder.mutation<any, { orderId: string; notes?: string }>({
      query: ({ orderId, notes }) => ({
        url: `/production-line/drawing/${orderId}/complete_drawing/`,
        method: 'POST',
        body: { notes },
      }),
      invalidatesTags: [
        'ProductionLineDrawing', 'ProductionLineDashboard',
        'Job', 'Order', 'ProductionJob'
      ],
    }),

    // ============================================================================
    // ORDERS ENDPOINTS
    // ============================================================================

    getJobOrders: builder.query<PaginatedResponse<OrderSummary> | OrderSummary[], string>({
      query: (jobId) => `/jobs/${jobId}/orders/?page_size=1000`,
      providesTags: (result, error, jobId) => [{ type: 'Order', id: `JOB_${jobId}` }],
      keepUnusedDataFor: 0, // Don't cache - always fetch fresh data
    }),

    createOrder: builder.mutation<Order, { jobId: string; data: OrderFormData }>({
      query: ({ jobId, data }) => ({ url: `/jobs/${jobId}/orders/`, method: 'POST', body: data }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: 'Order', id: `JOB_${jobId}` },
        'Order',
        // Invalidate production job cache since order changes affect job_type
        { type: 'ProductionJob', id: jobId },
        'ProductionJob',
        'ProductionSummary',
        // Also invalidate production line caches
        'ProductionLineSealing',
        'ProductionLineGlassCutting',
        'ProductionLineSpacer',
        'ProductionLineDrawing',
        'ProductionLineDashboard',
        'GlassArrival'
      ],
    }),

    getOrder: builder.query<Order, string>({
      query: (id) => `/orders/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Order', id }],
    }),

    updateOrder: builder.mutation<Order, { id: string; data: Partial<OrderFormData> }>({
      query: ({ id, data }) => ({ url: `/orders/${id}/`, method: 'PATCH', body: data }),
      invalidatesTags: (result, error, { id }) => {
        const jobId = result?.job;
        return [
          { type: 'Order', id },
          'Order',
          // Invalidate production job cache since order changes affect job_type
          ...(jobId ? [{ type: 'ProductionJob', id: jobId } as const] : []),
          'ProductionJob',
          'ProductionSummary',
          // Also invalidate production line caches since orders are displayed there
          'ProductionLineSealing',
          'ProductionLineGlassCutting',
          'ProductionLineSpacer',
          'ProductionLineDrawing',
          'ProductionLineDashboard',
          // Also invalidate Glass Arrival cache
          'GlassArrival'
        ];
      },
    }),

    deleteOrder: builder.mutation<{ message?: string }, { id: string; jobId?: string }>({
      query: ({ id }) => ({ url: `/orders/${id}/`, method: 'DELETE' }),
      invalidatesTags: (result, error, { id, jobId }) => [
        { type: 'Order', id },
        ...(jobId ? [{ type: 'Order', id: `JOB_${jobId}` } as const] : []),
        'Order',
        // Invalidate production job cache since order deletion affects job_type
        ...(jobId ? [{ type: 'ProductionJob', id: jobId } as const] : []),
        'ProductionJob',
        'ProductionSummary',
        // Also invalidate production line caches
        'ProductionLineSealing',
        'ProductionLineGlassCutting',
        'ProductionLineSpacer',
        'ProductionLineDrawing',
        'ProductionLineDashboard',
        'GlassArrival'
      ],
    }),

    // Update order loaded status
    updateOrderLoaded: builder.mutation<Order, { orderId: string; loaded: boolean }>({
      query: ({ orderId, loaded }) => ({
        url: `/orders/${orderId}/update_loaded/`,
        method: 'POST',
        body: { loaded },
      }),
      invalidatesTags: [
        'Order',
        'Job',
        'DeliveryJobs',
        'ProductionJob',
        'GlassArrival',
        'ProductionLineSealing',
        'ProductionLineGlassCutting',
        'ProductionLineSpacer',
        'ProductionLineDrawing',
      ],
    }),

    updateOrderLoadedOnPallet: builder.mutation<
      Order,
      { orderId: string; jobId: string; loadedOnPallet: boolean }
    >({
      query: ({ orderId, loadedOnPallet }) => ({
        url: `/orders/${orderId}/update_loaded_on_pallet/`,
        method: 'POST',
        body: { loaded_on_pallet: loadedOnPallet },
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: 'Order', id: `JOB_${jobId}` },
        'Order',
        'Job',
      ],
    }),

    // ============================================================================
    // ORDER IMAGES ENDPOINTS
    // ============================================================================
    presignOrderImageUpload: builder.mutation<{ url: string; fields: Record<string, string>; key: string }, { orderId: string; content_type: string; file_size: number; image_type: 'drawing'|'cutout'; feature: string }>(
      {
        query: ({ orderId, ...body }) => ({ url: `/orders/${orderId}/images/presign_upload`, method: 'POST', body }),
      }
    ),

    createOrderImage: builder.mutation<any, { orderId: string; body: any }>(
      {
        query: ({ orderId, body }) => ({ url: `/orders/${orderId}/images/`, method: 'POST', body }),
        invalidatesTags: (result, error, { orderId }) => [{ type: 'OrderImage', id: `ORDER_${orderId}` }, 'OrderImage'],
      }
    ),

    listOrderImages: builder.query<
      { count: number; next: string | null; previous: string | null; results: any[] },
      { orderId: string; feature?: string }
    >({
      query: ({ orderId, feature }) => ({
        url: `/orders/${orderId}/images/`,
        params: {
          page_size: 500,
          ...(feature ? { feature } : {}),
        },
      }),
      transformResponse: (response: unknown) => {
        if (Array.isArray(response)) {
          const results = response;
          return { count: results.length, next: null, previous: null, results };
        }
        const r = response as { count?: number; next?: string | null; previous?: string | null; results?: any[] };
        const results = Array.isArray(r?.results) ? r.results : [];
        return {
          count: r?.count ?? results.length,
          next: r?.next ?? null,
          previous: r?.previous ?? null,
          results,
        };
      },
      providesTags: (result, error, { orderId }) => [{ type: 'OrderImage', id: `ORDER_${orderId}` }],
    }),

    presignOrderImageDownload: builder.query<{ url: string }, { imageId: string }>(
      {
        query: ({ imageId }) => `/order-images/${imageId}/presign_download`,
      }
    ),

    deleteOrderImage: builder.mutation<{ message?: string }, { imageId: string; orderId?: string }>(
      {
        query: ({ imageId }) => ({ url: `/order-images/${imageId}/`, method: 'DELETE' }),
        invalidatesTags: (result, error, { orderId }) => orderId ? [{ type: 'OrderImage', id: `ORDER_${orderId}` }] : ['OrderImage'],
      }
    ),


    // ============================================================================
    // GLASS VALUES ENDPOINTS
    // ============================================================================

    getGlassValues: builder.query<PaginatedResponse<import('../../utils/types').GlassValue>, {
      page?: number; page_size?: number; search?: string;
      is_arrival?: boolean; is_cutting?: boolean;
      applies_to_single?: boolean; applies_to_double?: boolean; applies_to_triple?: boolean; applies_to_mirror?: boolean;
      supports_patterned_options?: boolean;
      ordering?: string;
    }>({
      query: (params = {}) => ({ url: '/glass-values/', params }),
      providesTags: ['Order', 'GlassValue'],
    }),

    getGlassValue: builder.query<import('../../utils/types').GlassValueDetail, string>({
      query: (id) => `/glass-values/${id}/`,
      providesTags: (result, error, id) => [{ type: 'GlassValue', id }],
    }),

    getGlassValuesDropdown: builder.query<{ results: import('../../utils/types').GlassValueDropdownOption[]; count: number }, { product_type?: 'single'|'double'|'triple'|'mirror'; glass_value?: 'arrival'|'cutting' }>({
      query: (params = {}) => ({ url: '/glass-values/dropdown/', params }),
      providesTags: ['Order', 'GlassValue'],
    }),

    getGlassValuePatternedOptions: builder.query<
      { results: import('../../utils/types').PatternedGlassOptionRow[]; count: number },
      string
    >({
      query: (glassValueId) => `/glass-values/${glassValueId}/patterned-options/`,
      providesTags: ['Order', 'GlassValue'],
    }),

    getPatternedGlassOption: builder.query<import('../../utils/types').PatternedGlassOptionPublic, string>({
      query: (id) => `/patterned-glass-options/${id}/`,
      providesTags: ['Order', 'GlassValue'],
      keepUnusedDataFor: 600,
    }),

    createGlassValue: builder.mutation<import('../../utils/types').GlassValue, import('../../utils/types').GlassValueWritePayload>({
      query: (data) => ({ url: '/glass-values/', method: 'POST', body: data }),
      invalidatesTags: ['Order', 'GlassValue'],
    }),

    updateGlassValue: builder.mutation<import('../../utils/types').GlassValue, { id: string; data: Partial<import('../../utils/types').GlassValueWritePayload> }>({
      query: ({ id, data }) => ({ url: `/glass-values/${id}/`, method: 'PATCH', body: data }),
      invalidatesTags: (result, error, { id }) => ['Order', 'GlassValue', { type: 'GlassValue', id }],
    }),

    deleteGlassValue: builder.mutation<{ message?: string }, string>({
      query: (id) => ({ url: `/glass-values/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['Order', 'GlassValue'],
    }),

    // ============================================================================
    // DELIVERY POSTCODES
    // ============================================================================

    getGroupedDeliveryPostcodes: builder.query<GroupedDeliveryPostcodesResponse, void>({
      query: () => '/delivery-postcodes/grouped/',
      providesTags: ['DeliveryPostcode'],
    }),

    bulkAddDeliveryPostcodes: builder.mutation<
      BulkAddDeliveryPostcodesResponse,
      BulkAddDeliveryPostcodesRequest
    >({
      query: (body) => ({
        url: '/delivery-postcodes/bulk-add/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DeliveryPostcode'],
    }),

    updateDeliveryPostcode: builder.mutation<
      DeliveryPostcodeDetail,
      { id: string; data: Partial<{ weekday: number; postcode: string }> }
    >({
      query: ({ id, data }) => ({
        url: `/delivery-postcodes/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['DeliveryPostcode'],
    }),

    deleteDeliveryPostcode: builder.mutation<void, string>({
      query: (id) => ({ url: `/delivery-postcodes/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['DeliveryPostcode'],
    }),

    // ============================================================================
    // EMAIL SYSTEM ENDPOINTS
    // ============================================================================

    // Get available email accounts (for frontend selection)
    getEmailAccounts: builder.query<EmailAccount[], void>({
      query: () => '/emails/accounts/available/',
      providesTags: ['EmailAccount'],
    }),

    // Get available email templates (for frontend selection)
    getEmailTemplates: builder.query<EmailTemplate[], void>({
      query: () => '/emails/templates/available/',
      providesTags: ['EmailTemplate'],
    }),

    // Send email
    sendEmail: builder.mutation<SendEmailResponse, SendEmailRequest>({
      query: (data) => ({
        url: '/emails/actions/send/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['EmailLog'],
    }),

    // Send test email
    sendTestEmail: builder.mutation<SendEmailResponse, SendTestEmailRequest>({
      query: (data) => ({
        url: '/emails/actions/send_test/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['EmailLog'],
    }),

    // Get email status
    getEmailStatus: builder.query<EmailLog, { email_log_id: string }>({
      query: ({ email_log_id }) => `/emails/actions/status/?email_log_id=${email_log_id}`,
      providesTags: (result, error, { email_log_id }) => [{ type: 'EmailLog', id: email_log_id }],
    }),

    // Get email logs (for monitoring)
    getEmailLogs: builder.query<PaginatedResponse<EmailLog>, {
      page?: number;
      page_size?: number;
      status?: string;
      recipient?: string;
      template?: string;
    }>({
      query: (params = {}) => ({
        url: '/emails/logs/',
        params: Object.fromEntries(
          Object.entries(params).filter(([_, value]) => value !== undefined)
        ),
      }),
      providesTags: ['EmailLog'],
    }),

    // Test PDF generation
    testPdfGeneration: builder.mutation<void, void>({
      query: () => ({
        url: '/emails/test/pdf/',
        method: 'GET',
        responseHandler: async (response) => {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'test_pdf.pdf';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        },
      }),
    }),

    // Quotes endpoints
    getQuotes: builder.query<PaginatedResponse<Quote>, {
      page?: number;
      page_size?: number;
      search?: string;
      status?: string;
    }>({
      query: (params = {}) => ({
        url: '/quotes/',
        params: Object.fromEntries(
          Object.entries(params).filter(([_, value]) => value !== undefined)
        ),
      }),
      providesTags: ['Quote'],
    }),

    getQuote: builder.query<Quote, string>({
      query: (id) => `/quotes/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Quote', id }],
    }),

    createQuote: builder.mutation<Quote, QuoteFormData>({
      query: (data) => ({
        url: '/quotes/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Quote'],
    }),

    updateQuote: builder.mutation<Quote, { id: string; data: Partial<QuoteFormData> }>({
      query: ({ id, data }) => ({
        url: `/quotes/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Quote', id }, 'Quote'],
    }),

    deleteQuote: builder.mutation<void, string>({
      query: (id) => ({
        url: `/quotes/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Quote'],
    }),

    regenerateQuotePdf: builder.mutation<Quote, string>({
      query: (id) => ({
        url: `/quotes/${id}/regenerate_pdf/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Quote', id }],
    }),

    updateQuoteFlags: builder.mutation<Quote, { id: string; is_paid?: boolean; is_job_added?: boolean }>({
      query: ({ id, is_paid, is_job_added }) => ({
        url: `/quotes/${id}/update_flags/`,
        method: 'PATCH',
        body: { is_paid, is_job_added },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Quote', id }, 'Quote'],
    }),

    sendQuoteEmail: builder.mutation<{ email_log_id: string; message: string }, string>({
      query: (id) => ({
        url: `/quotes/${id}/send_quote_email/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Quote', id }, 'Quote'],
    }),

    // Send order notification email (review or final)
    sendOrderNotificationEmail: builder.mutation<SendEmailResponse, SendOrderNotificationRequest>({
      query: (data) => ({
        url: '/emails/actions/send_order_notification/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['EmailLog'],
      async onQueryStarted({ job_id, email_type }, { dispatch, queryFulfilled, getState }) {
        if (email_type !== 'final') {
          return;
        }
        try {
          await queryFulfilled;
          if (!prodMergeApi) return;
          const job = await dispatch(
            prodMergeApi.endpoints.getJob.initiate(job_id, {
              forceRefetch: true,
              subscribe: false,
            })
          ).unwrap();
          const queryArgs = prodMergeApi.util.selectCachedArgsForQuery(
            getState(),
            'getProductionJobs'
          ) as GetProductionJobsParams[];
          for (const qa of queryArgs) {
            dispatch(
              prodMergeApi.util.updateQueryData(
                'getProductionJobs',
                qa,
                (draft: PaginatedResponse<ProductionJobSummary>) => {
                  const row = draft.results?.find((j) => j.id === job_id);
                  if (row) mergeProductionSummaryFromJob(row, job);
                }
              )
            );
          }
        } catch {
          /* send failed or job fetch failed */
        }
      },
    }),

    // Send in-production notification email
    sendInProductionNotificationEmail: builder.mutation<
      SendEmailResponse,
      { job_id: string; account_id?: string; use_actual_manufacture_date?: boolean }
    >({
      query: (data) => ({
        url: '/emails/actions/send_in_production_notification/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['EmailLog', 'Job', 'ProductionJob'],
    }),

    // Send delivery schedule email
    sendDeliveryScheduleEmail: builder.mutation<SendEmailResponse, { job_id: string; account_id?: string }>({
      query: (data) => ({
        url: '/emails/actions/send_delivery_schedule/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['EmailLog', 'Job'],
    }),

    // ============================================================================
    // HOLIDAY MANAGEMENT ENDPOINTS
    // ============================================================================

    // Bank Holidays
    getBankHolidays: builder.query<BankHoliday[], void>({
      query: () => ({
        url: '/bank-holidays/',
        params: { page_size: HOLIDAY_FETCH_PAGE_SIZE },
      }),
      transformResponse: normalizeBankHolidaysList,
      providesTags: ['BankHoliday'],
    }),

    createBankHoliday: builder.mutation<BankHoliday, BankHolidayFormData>({
      query: (data) => ({
        url: '/bank-holidays/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['BankHoliday'],
    }),

    updateBankHoliday: builder.mutation<BankHoliday, { id: string; data: Partial<BankHolidayFormData> }>({
      query: ({ id, data }) => ({
        url: `/bank-holidays/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['BankHoliday'],
    }),

    deleteBankHoliday: builder.mutation<void, string>({
      query: (id) => ({
        url: `/bank-holidays/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['BankHoliday'],
    }),

    // Holiday Requests
    getHolidayRequests: builder.query<PaginatedResponse<HolidayRequest>, {
      year?: number;
      status?: string;
      user_id?: number;
      page_size?: number;
    }>({
      query: (params = {}) => ({
        url: '/holiday-requests/',
        params: Object.fromEntries(
          Object.entries(params).filter(([_, value]) => value !== undefined)
        ),
      }),
      providesTags: ['HolidayRequest'],
    }),

    getMyHolidayRequests: builder.query<MyHolidayRequestsResponse, { year?: number }>({
      query: (params = {}) => ({
        url: '/holiday-requests/my_requests/',
        params,
      }),
      providesTags: ['HolidayRequest'],
    }),

    createHolidayRequest: builder.mutation<HolidayRequest, HolidayRequestFormData>({
      query: (data) => ({
        url: '/holiday-requests/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['HolidayRequest', 'HolidayStats'],
    }),

    createHolidayRequestForUser: builder.mutation<HolidayRequest, AdminHolidayRequestFormData>({
      query: (data) => ({
        url: '/holiday-requests/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['HolidayRequest', 'HolidayStats'],
    }),

    // Holiday Stats
    getMyHolidayStats: builder.query<UserHolidayStats, { year?: number }>({
      query: (params = {}) => ({
        url: '/holiday-requests/my_stats/',
        params,
      }),
      providesTags: ['HolidayStats'],
    }),

    getAllUsersHolidayStats: builder.query<AllUsersHolidayStatsResponse, { year?: number }>({
      query: (params = {}) => ({
        url: '/holiday-requests/all_stats/',
        params,
      }),
      providesTags: ['HolidayStats'],
    }),

    // Approve/Reject Holiday Requests
    approveHolidayRequest: builder.mutation<HolidayRequest, { id: string; notes?: string }>({
      query: ({ id, notes }) => ({
        url: `/holiday-requests/${id}/approve/`,
        method: 'POST',
        body: { notes },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'HolidayRequest', id },
        'HolidayRequest',
        'HolidayStats'
      ],
    }),

    rejectHolidayRequest: builder.mutation<HolidayRequest, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/holiday-requests/${id}/reject/`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'HolidayRequest', id },
        'HolidayRequest',
        'HolidayStats'
      ],
    }),

    // Toggle status between approved/rejected (admin only)
    toggleHolidayRequestStatus: builder.mutation<HolidayRequest, { id: string; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `/holiday-requests/${id}/toggle_status/`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'HolidayRequest', id },
        'HolidayRequest',
        'HolidayStats'
      ],
    }),

  }),
});

prodMergeApi = apiSlice;

export const {
  useGetHealthQuery,
  useLoginMutation,
  useLogoutMutation,
  useGetUserProfileQuery,
  useRefreshTokenMutation,
  // Role management hooks
  useGetRolesQuery,
  useGetRoleQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetRolePermissionsQuery,
  useAssignRolePermissionsMutation,
  useGetRoleUsersQuery,
  // Permission management hooks
  useGetPermissionsQuery,
  useGetPermissionQuery,
  useGetPermissionCategoriesQuery,
  useGetPermissionsByCategoryQuery,
  // User profile management hooks
  useGetUserProfilesQuery,
  useGetUserProfileByIdQuery,
  useCreateUserProfileMutation,
  useUpdateUserProfileMutation,
  useDeleteUserProfileMutation,
  useAssignUserRoleMutation,
  useChangeUserStatusMutation,
  useSoftDeleteUserProfileMutation,
  // User activity hooks
  useGetUserActivitiesQuery,
  useGetActivitySummaryQuery,
  // Validation
  useValidateUsernameQuery,
  useValidateEmailQuery,
  // Color hooks
  useGetColorsQuery,
  useGetAvailableColorsQuery,
  useAssignUserColorMutation,
  useRemoveUserColorMutation,
  // Jobs hooks
  useGetJobsQuery,
  useGetJobQuery,
  useCreateJobMutation,
  useUpdateJobMutation,
  usePatchJobMutation,
  useDeleteJobMutation,
  useRemakeJobMutation,
  useAssignDeliveryMutation,
  useUpdateJobStatusMutation,
  useAddJobNoteMutation,
  // Calendar jobs hooks
  useGetCalendarJobsQuery,
  useRescheduleJobMutation,
  useLockJobMutation,
  // Production hooks
  useGetProductionJobsQuery,
  useGetProductionSummaryQuery,
  useMarkPalletBookedMutation,
  useMoveToProductionMutation,
  useUpdateProductionStatusMutation,
  useCompleteProductionMutation,
  useMoveBackToProductionMutation,

  // Production Line hooks
  useGetProductionLineDashboardQuery,
  useBulkCompleteProductionLineDashboardMutation,
  useGetProductionLineDashboardAnalyticsQuery,
  useGetProductionLineSealingQuery,
  useGetProductionLineGlassCuttingQuery,
  useGetProductionLineSpacerQuery,
  useGetProductionLineDrawingQuery,
  // Glass Arrival hooks
  useGetGlassArrivalJobsQuery,
  // Delivery Jobs hooks
  useGetDeliveryJobsQuery,
  useBulkRescheduleDeliveryJobsMutation,
  useUpdateDeliveryDetailsMutation,
  // Delivery Workflow hooks
  useGetCollectionStatusQuery,
  useSetCollectionCompletedMutation,
  useGetDeliveryStatusQuery,
  useSubmitDeliveryCompleteMutation,
  useSubmitDeliveryIncompleteMutation,
  useUpdateDeliveryRecordMutation,
  useGetDeliveryHistoryQuery,
  useOverrideDeliveryStatusMutation,
  usePresignDeliveryUploadMutation,
  usePresignDeliveryPhotoMutation,
  useCompleteSealingMutation,
  useSendBackSealingMutation,
  useUpdateCuttingSpecsMutation,
  useCompleteCuttingMutation,
  useSendBackGlassCuttingMutation,
  useCompleteSpacerCuttingMutation,
  useAttachDrawingMutation,
  useCompleteDrawingMutation,

  // Orders hooks
  useGetJobOrdersQuery,
  useCreateOrderMutation,
  useGetOrderQuery,
  useUpdateOrderMutation,
  useDeleteOrderMutation,
  useUpdateOrderLoadedMutation,
  useUpdateOrderLoadedOnPalletMutation,
  // Order images
  usePresignOrderImageUploadMutation,
  useCreateOrderImageMutation,
  useListOrderImagesQuery,
  useLazyListOrderImagesQuery,
  usePresignOrderImageDownloadQuery,
  useDeleteOrderImageMutation,
  // Glass Values hooks
  useGetGlassValuesQuery,
  useGetGlassValueQuery,
  useGetGlassValuesDropdownQuery,
  useGetGlassValuePatternedOptionsQuery,
  useGetPatternedGlassOptionQuery,
  useCreateGlassValueMutation,
  useUpdateGlassValueMutation,
  useDeleteGlassValueMutation,
  useGetGroupedDeliveryPostcodesQuery,
  useBulkAddDeliveryPostcodesMutation,
  useUpdateDeliveryPostcodeMutation,
  useDeleteDeliveryPostcodeMutation,
  // Email System hooks
  useGetEmailAccountsQuery,
  useGetEmailTemplatesQuery,
  useSendEmailMutation,
  useSendTestEmailMutation,
  useSendOrderNotificationEmailMutation,
  useTestPdfGenerationMutation,
  useSendInProductionNotificationEmailMutation,
  useSendDeliveryScheduleEmailMutation,
  useGetEmailStatusQuery,
  useGetEmailLogsQuery,
  // Quotes hooks
  useGetQuotesQuery,
  useGetQuoteQuery,
  useCreateQuoteMutation,
  useUpdateQuoteMutation,
  useDeleteQuoteMutation,
  useRegenerateQuotePdfMutation,
  useUpdateQuoteFlagsMutation,
  useSendQuoteEmailMutation,
  // Holiday hooks
  useGetBankHolidaysQuery,
  useCreateBankHolidayMutation,
  useUpdateBankHolidayMutation,
  useDeleteBankHolidayMutation,
  useGetHolidayRequestsQuery,
  useGetMyHolidayRequestsQuery,
  useCreateHolidayRequestMutation,
  useCreateHolidayRequestForUserMutation,
  useGetMyHolidayStatsQuery,
  useGetAllUsersHolidayStatsQuery,
  useApproveHolidayRequestMutation,
  useRejectHolidayRequestMutation,
  useToggleHolidayRequestStatusMutation,
} = apiSlice;
